import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import {
    Button,
    Box,
    Switch,
    Typography,
    Divider,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Slider,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import SettingsIcon from '@mui/icons-material/Settings';
import Brightness6Icon from '@mui/icons-material/Brightness6';
import ContrastIcon from '@mui/icons-material/Contrast';
import PaletteIcon from '@mui/icons-material/Palette';
import OpacityIcon from '@mui/icons-material/Opacity';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTheme } from '@mui/material/styles';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fiderIcon, ktpIcon, opnIcon, pillarIcon, planIcon, projectStatusOreolIcon, rlndIcon, runnIcon, zvzIcon } from '../layers/icons';
import { setBaseLayer as setBaseLayerRedux, setDataLayer as setDataLayerRedux, setOverlayLayer as setOverlayLayerRedux, setMapSettings } from '../../store/reducers/helpersReducer';
import { Verified, Warning, Highlight } from '@mui/icons-material';
import DashboardIcon from '@mui/icons-material/Dashboard';

const numberFormat = new Intl.NumberFormat('ru-RU')



const ModalLayers = ({ buttonPosition }) => {
    const map = useMap();
    const theme = useTheme();
    const dispatch = useDispatch();

    const { electricity, distances } = useSelector(
        (state) => {
            const electricity = state.state.electricity
            const distances = state.helper.distances
            return {
                electricity,
                distances
            }
        }, (prev, next) => {
            return prev.electricity === next.electricity &&
                prev.distances === next.distances
        }
    );
    const tasks = useSelector(state => state.tasks.tasks)
    const stageFilter = useSelector(state => state.state.filter.stage)
    const { baseLayer: reduxBaseLayer, overlayLayers: reduxOverlayLayers, dataLayers: reduxDataLayers, settings: reduxSettings } = useSelector(state => state.helper.layers)
    const [baseLayer, setBaseLayer] = useState(reduxBaseLayer || 1)
    const [overlayLayers, setOverlayLayers] = useState(reduxOverlayLayers || '{}')
    const [dataLayers, setDataLayers] = useState(reduxDataLayers || {})
    const [settingsAnchorEl, setSettingsAnchorEl] = useState(null)
    const [settings, setSettings] = useState(reduxSettings || { brightness: 100, contrast: 100, saturation: 100, opacity: 100 })
    const user = useSelector(state => state.state.user)




    const applyFilters = (newSettings) => {
        const { brightness, contrast, saturation, opacity } = newSettings || { brightness: 100, contrast: 100, saturation: 100, opacity: 100 };
        const filterValue = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) opacity(${opacity}%)`;
        // // Ищем YandexMutantLayer контейнер
        const yandexContainer = map.getContainer().querySelector('.leaflet-yandex-mutant');
        if (yandexContainer) {
            yandexContainer.style.filter = filterValue;
            return
        }
        const tilePane = map.getContainer().querySelector('.leaflet-tile-pane');
        if (tilePane) {
            tilePane.style.filter = filterValue
            return
        }

    };

    function tasksCustomSwtich() {
        // Маппинг статусов задач с их цветами из icons.js
        const taskStatuses = [
            { status: 'new', color: '#7CB342', label: 'Новая' },
            { status: 'progress', color: '#3874cb', label: 'В работе' },
            { status: 'paused', color: '#d55c26', label: 'Отложено' },
            { status: 'done', color: '#457b3b', label: 'Завершено' },
            { status: 'review', color: '#ea33f7', label: 'На проверке' },
            { status: 'cancelled', color: '#424242', label: 'Отменено' }
        ];

        // Функция для затемнения цвета (для выключенного состояния)
        const darkenColor = (color, opacity = 0.3) => {
            const num = parseInt(color.replace("#", ""), 16);
            const R = (num >> 16) & 255;
            const G = (num >> 8) & 255;
            const B = num & 255;
            return `rgba(${R}, ${G}, ${B}, ${opacity})`;
        };

        return (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
                {taskStatuses.map(({ status, color, label }) => {
                    const layerKey = `tasks:${status}`;
                    const isActive = Boolean(dataLayers[layerKey]);
                    const displayColor = isActive ? color : darkenColor(color, 0.3);

                    return (
                        <Box
                            key={status}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDataLayerChange(layerKey);
                            }}
                            sx={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: displayColor,
                                cursor: 'pointer',
                                border: `2px solid ${isActive ? color : '#888'}`,
                                transition: 'all 0.2s ease',
                                opacity: isActive ? 1 : 0.5,
                                '&:hover': {
                                    transform: 'scale(1.15)',
                                    boxShadow: `0 0 8px ${color}60`,
                                    opacity: 1
                                }
                            }}
                            title={label}
                        />
                    );
                })}
            </Box>
        )
    }


    const baseLayers = [
        { id: 1, name: 'Карта(OSM)' },
        { id: 2, name: 'Карта(2GIS)' },
        { id: 3, name: 'Карта(Яндекс)' },
        { id: 4, name: 'Спутник(Яндекс)' },
        { id: 5, name: 'Гибрид(Яндекс)' },
        { id: 6, name: 'Карта(Google)' },
        { id: 7, name: 'Спутник(Google)' },
        { id: 8, name: 'Гибрид(Google)' },
        { id: 9, name: 'Белый фон' }
    ];

    const overlayLayersConfig = [
        {
            key: 'res',
            name: 'Границы РЭС',
            icon:
                <svg style={{ marginTop: '3px' }} width="18px" height="18px" viewBox="-10 -10 200 200" xmlns="http://www.w3.org/2000/svg">
                    <rect x="0" y="0" width="180" height="180" rx="20" stroke="#fdbf6f" strokeWidth="20" fill="none" />
                </svg>,
            count: `${electricity?.res?.length || 0} шт`
        },
        {
            key: 'powerPlants',
            name: 'Электростанции',
            icon: <img data-type="legend-view" data-bind="attr: { src: (imageSource.contentType ? 'data:' + imageSource.contentType + ';base64,' : '') + imageSource.getUrl() }" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAABn0lEQVQ4ja2Vv0vDQBTHszk5CVIluYA66Z/h4j8gqKO7Uk0u6aKgqJ1FBwfp4CLE0txFBxcFfyzaLnbWsaCzLrrU9y6pJunlmkAPvvT1XfJ5ufdeXjTiBtdm5ao7DCFLQ8Nwgked8r24iBPcp32qfWQgSwDRocWW6fAdTbFk+8iQAmUXT2768yhVMCmw7+KFwxFCeZW4l10hyh/QlwuYhhlr59PE4c8i2ZS3wf6I7Kfxcn0qDcSc/gHxTzJaYxkAX+D/1iljALkzKNsVtsN/wP+pW2wpnibpkcdWT0fh5loUpGNQ/7gH+xf4YC962hqhfjUzhyWrboYw/qrbbL8fFkq3/QPYezMhrxPrHlFWGWAt6Kn3LFhPYUp4U1llUQybVTAyHi0TZvNmeGTfzQM8Em8PZTdSGD65w2/FfRveTK4+hHeyHRZFAhPHDTqgl9x9SCjbFn6bnUGrnKAA1hI2+KLCbeXuQ6N8MZeYJJDT9GQpWd7swD5MRKR8EdpjBYrTwN+4cC8OUxYlvopMnYHAoiMsAUwP2KLDNTFgh/0J+AX00TU9BokJrAAAAABJRU5ErkJggg==" />,
            count: `${electricity?.powerPlants?.length || 0} шт`
        },
        {
            key: 'feedingSubstations',
            name: 'Подстанции',
            icon: <img data-type="legend-view" data-bind="attr: { src: (imageSource.contentType ? 'data:' + imageSource.contentType + ';base64,' : '') + imageSource.getUrl() }" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAABOklEQVQ4ja2VsW7CMBCGPTF26iMgJDsMcXjACpShEiysEAd2nqIIMdMOSLwBCyMz3BmcOM6dgQpLv+LElz+f706OyEq1Hyz7l3cIvcR9sgHlvvQiWYfPHqxv0MsZ5sIbgzIZisig1tGDNKSCe5PeJyr2MdKQC9ZGjVEx8pYhF4xkYHbQRp4cJflRyGlliDdsIJBlRu51IVc4p2IQhs1hm06e0pnK0wIqC6QhpdvZU4aZUVtLViSjm5qUfpoeGuq5LcQB6ZyhTxnmPGqIwbayDbqaEslbu4n1oausT1fJqDWVy2gfcnRA9kPlkjR0ZhxdbdbMZZVzrg8pOt+Mqjjbh5auVGcw+IXrzsqoYzX3ZdQfxmI38EX5Ep3utPvhlM7lt38fCtfxnaf68NUjLDRsHLD/OFzrA/bdv4Arfje8TgtGuk0AAAAASUVORK5CYII=" />,
            count: `${electricity?.feedingSubstations?.length || 0} шт`
        },
        {
            key: 'transformers',
            name: 'ТП 6(10)/0,4 кВ',
            icon: ktpIcon('default', 24, 24),
            count: `${electricity?.transformers?.length || 0} шт`
        },
        {
            key: 'fider330',
            name: 'Фидер 330кВ',
            icon: fiderIcon('fider330', 24),
            count: `${numberFormat.format(distances?.fider330) || 0} км`
        },
        {
            key: 'fider110',
            name: 'Фидер 110кВ',
            icon: fiderIcon('fider110', 23),
            count: `${numberFormat.format(distances?.fider110) || 0} км`
        },
        {
            key: 'fider35',
            name: 'Фидер 35кВ',
            icon: fiderIcon('fider35', 22),
            count: `${numberFormat.format(distances?.fider35) || 0} км`
        },
        {
            key: 'fider10',
            name: 'Фидер 10кВ',
            icon: fiderIcon('fider_sip_3', 21),
            count: `${numberFormat.format(distances?.fider10) || 0} м`
        },
        {
            key: 'fider10_04',
            name: 'Фидер 10-0,4кВ',
            icon: fiderIcon('fider_sip_3', 21),
            count: `${numberFormat.format(distances['fider10-04']) || 0} м`
        },
        {
            key: 'fider6',
            name: 'Фидер 6кВ',
            icon: fiderIcon('fider_sip_3', 21),
            count: `${numberFormat.format(distances?.fider6) || 0} м`
        },
        {
            key: 'fider04',
            name: 'Линия 0,4кВ',
            icon: fiderIcon('fider_sip_2', 20),
            count: `${numberFormat.format(distances?.fider04) || 0} м`
        },
        {
            key: 'pillar10',
            name: 'Опора 6(10)кВ',
            icon: pillarIcon('pillar10', 'default', 2, 1, 15),
            count: `${numberFormat.format(electricity?.pillars?.filter(p => p.type === 'pillar10').length) || 0} шт`
        },
        {
            key: 'pillar04_10',
            name: 'Опора 0,4-6(10)кВ',
            icon: pillarIcon('pillar04-10', 'default', 2, 1, 15),
            count: `${numberFormat.format(electricity?.pillars?.filter(p => p.type === 'pillar04-10').length) || 0} шт`
        },
        {
            key: 'pillar04',
            name: 'Опора 0,4кВ',
            icon: pillarIcon('pillar04', 'default', 2, 1, 15),
            count: `${numberFormat.format(electricity?.pillars?.filter(p => p.type === 'pillar04').length) || 0} шт`
        },

        {
            key: 'zvz',
            name: 'Комплект адаптеров ВЗ',
            icon: zvzIcon(22),
            count: `${electricity?.pillarsZvz?.length || 0} шт`
        },
        {
            key: 'opn',
            name: 'Комплект ОПН',
            icon: opnIcon(20),
            count: `${electricity?.pillarsOpn?.length || 0} шт`
        },
        {
            key: 'runn',
            name: 'РУНН',
            icon: runnIcon(20),
            count: `${electricity?.pillarsRunn?.length || 0} шт`
        },
        {
            key: 'rlnd',
            name: 'РЛНД',
            icon: rlndIcon(18),
            count: `${electricity?.pillarsRlnd?.length || 0} шт`
        }
    ];

    const dataLayersConfig = [
        {
            key: 'viewTasks',
            name: 'Задачи',
            customSwitch: tasksCustomSwtich,
            hide: false,
            icon: <DashboardIcon sx={{ fontSize: 20, color: 'rgb(0, 174, 255)' }} />,
            count: `${tasks.filter(t => t.type === 'points')?.length || 0} шт`
        },
        {
            key: 'viewPlan',
            name: 'План',
            hide: false,
            icon: planIcon(22),
            count: `${tasks.filter(t => t.type === 'contour')?.length || 0} шт`
        },
        {
            key: 'viewProjectStatus',
            name: 'Проектный статус',
            hide: false,
            icon: projectStatusOreolIcon('pillar', 2, 20),
            count: null
        },
        {
            key: 'viewMounted',
            name: 'Выполнение',
            hide: !stageFilter?.some(f => f === 4 || f === 3),
            disable: dataLayers.viewNotSipClass,
            icon: <Verified sx={{ fontSize: '20px', color: '#78A75A' }} />,
            count: null
        },
        {
            key: 'viewNotSipClass',
            name: 'Не задан СИП-класс',
            hide: !user.roles.includes('editor'),
            disable: dataLayers.viewMounted,
            icon: <Warning sx={{ fontSize: '20px', color: '#FF9800' }} />,
            count: null
        },
        {
            key: 'viewStagesBackLight',
            name: 'Подсветка этапов',
            hide: true,
            disable: false,
            icon: <Highlight sx={{ fontSize: '20px', color: '#9C27B0' }} />,
            count: null
        }
    ]

    // Инициализация localStorage при первом запуске
    // useEffect(() => {
    //     const defaultValues = {
    //         'base_layer': '1',
    //         'layer_res': '1',
    //         'layer_powerPlants': '1',
    //         'layer_feedingSubstations': '1',
    //         'layer_transformers': '1',
    //         'layer_fider330': '1',
    //         'layer_fider110': '1',
    //         'layer_fider35': '1',
    //         'layer_fider10': '1',
    //         'layer_fider04_10': '1',
    //         'layer_fider04': '1',
    //         'layer_pillar04_10': '1',
    //         'layer_vehivles': '1'
    //     };

    //     Object.entries(defaultValues).forEach(([key, value]) => {
    //         if (localStorage.getItem(key) === null) {
    //             localStorage.setItem(key, value);
    //         }
    //     });
    // }, []);

    useEffect(() => {
        map.on('baselayerchange', (e) => {
            console.log('baselayerchange', e)
            setTimeout(() => dispatch(setBaseLayerRedux(e.id)), 100)
            setTimeout(() => applyFilters(settings), 1000)
        })
        map.on('overlaylayerchange', (e) => {
            console.log('overlaylayerchange', e)
            setTimeout(() => dispatch(setOverlayLayerRedux({ name: e.name, state: e.state })), 100)
        })
        map.on('datalayerchange', (e) => {
            console.log('datalayerchange', e)
            setTimeout(() => dispatch(setDataLayerRedux(e)), 100)
        })
    }, [])

    const handleBaseLayerChange = (layerId) => {
        setBaseLayer(layerId);
        map.fire('baselayerchange', { id: layerId });
    };

    const handleOverlayLayerChange = (layerKey) => {
        console.log(layerKey)
        const currentState = Boolean(overlayLayers[layerKey]);
        const newState = !currentState;
        setOverlayLayers(prev => ({ ...prev, [layerKey]: newState }));
        // setTimeout(() => dispatch(setOverlayLayer({ name: layerKey, state: newState })), 100)
        map.fire('overlaylayerchange', { name: layerKey, state: newState });
    };

    const handleDataLayerChange = (layerKey) => {
        const currentState = Boolean(dataLayers[layerKey]);
        const newState = !currentState;
        setDataLayers(prev => ({ ...prev, [layerKey]: newState }));
        // setTimeout(() => setBaseLayerRedux({ name: layerKey, state: newState }), 100)
        map.fire('datalayerchange', { name: layerKey, state: newState });

    };

    const handleSettingsClick = (event) => {
        event.stopPropagation();
        setSettingsAnchorEl(event.currentTarget);
    };

    const handleSettingsClose = () => {
        setSettingsAnchorEl(null);
    };

    const handleSettingChange = (key, value) => {
        const newSettings = { ...settings, [key]: value };
        applyFilters(newSettings)
        setSettings(newSettings);
        setTimeout(() => dispatch(setMapSettings(newSettings)), 1000)

    };

    // Вычисляем стили позиционирования на основе позиции кнопки
    const dialogBoxStyles = useMemo(() => {
        console.log(buttonPosition)
        const baseStyles = {
            position: 'absolute',
            zIndex: 1202,
            backgroundColor: '#1a1a1a',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '1px solid #333',
            overflow: 'hidden',
            transition: 'all 0.3s ease-in-out',
            color: '#ffffff',
            opacity: 0.95,
            p: 1,
            transformOrigin: 'left top',
            transform: window.innerWidth < 768 ? 'scale(0.8)' : 'scale(1)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
                width: '12px',
                height: '8px'
            },
            '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(255, 255, 255, 0.4)'
            },
        };

        const responsiveStyles = {
            ...baseStyles,
            width: 'auto',
            minWidth: '280px',
            maxWidth: '380px',
            height: 'auto',
            maxHeight: '80vh',
            borderRadius: 2
        };

        // Вычисляем позицию на основе позиции кнопки
        if (buttonPosition) {
            console.log(buttonPosition);
            const modalWidth = 300;
            const modalHeight = 600;

            // Позиционируем справа от кнопки с отступом
            let left = buttonPosition.right + 8;
            let top = buttonPosition.top;

            // Проверяем, не выходит ли модальное окно за правый край экрана
            // if (left + modalWidth > window.innerWidth) {
            //     // Позиционируем слева от кнопки
            //     left = buttonPosition.left - modalWidth - 10;
            // }

            // Проверяем, не выходит ли за левый край
            // if (left < 10) {
            //     left = 10;
            // }

            // Проверяем, не выходит ли за нижний край экрана
            // if (top + modalHeight > window.innerHeight) {
            //     top = window.innerHeight - modalHeight - 10;
            // }

            // Проверяем, не выходит ли за верхний край
            // if (top < 10) {
            //     top = 10;
            // }

            return {
                ...responsiveStyles,
                left: `${left}px`,
                top: `${top}px`,
                margin: 0
            };
        }

        // Дефолтное позиционирование (если buttonPosition не передан)
        return {
            ...responsiveStyles,
            margin: '10px 10px 5px 5px'
        };
    }, [buttonPosition]);

    return (
        createPortal(
            <Box sx={dialogBoxStyles}>
                {/* Базовые слои */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" sx={{
                        fontWeight: 600,
                        display: 'flex',
                        justifyContent: 'start',
                        alignItems: 'center',
                        fontSize: '0.75rem',
                        color: '#b0b0b0'
                    }}>
                        <LayersIcon sx={{ fontSize: 12, mr: 0.3 }} />
                        Базовые слои
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={handleSettingsClick}
                        sx={{
                            color: '#b0b0b0',
                            padding: '2px',
                            '&:hover': {
                                backgroundColor: '#2a2a2a',
                            }
                        }}
                    >
                        <SettingsIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
                <Menu
                    anchorEl={settingsAnchorEl}
                    open={Boolean(settingsAnchorEl)}
                    onClose={handleSettingsClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            backgroundColor: '#1a1a1a',
                            color: '#ffffff',
                            minWidth: '240px',
                            maxWidth: '280px',
                            p: 1.5,
                            border: '1px solid #333',
                            borderRadius: '8px',
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, pb: 1, borderBottom: '1px solid #333' }}>
                        <Typography variant="caption" sx={{ color: '#e0e0e0', fontSize: '0.7rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <SettingsIcon sx={{ fontSize: 14 }} />
                            Настройки отображения
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Яркость */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Brightness6Icon sx={{ fontSize: 14, color: '#0078d4' }} />
                                    <Typography variant="caption" sx={{ color: '#b0b0b0', fontSize: '0.7rem' }}>
                                        Яркость
                                    </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: '#e0e0e0', fontSize: '0.7rem', fontWeight: 600, minWidth: '35px', textAlign: 'right' }}>
                                    {settings.brightness}%
                                </Typography>
                            </Box>
                            <Slider
                                value={settings.brightness}
                                onChange={(e, value) => handleSettingChange('brightness', value)}
                                min={0}
                                max={200}
                                step={1}
                                size="small"
                                sx={{
                                    color: '#0078d4',
                                    height: 4,
                                    '& .MuiSlider-thumb': {
                                        width: 14,
                                        height: 14,
                                        '&:hover': {
                                            boxShadow: '0 0 0 8px rgba(0, 120, 212, 0.16)',
                                        },
                                    },
                                    '& .MuiSlider-track': {
                                        height: 4,
                                    },
                                    '& .MuiSlider-rail': {
                                        height: 4,
                                        opacity: 0.3,
                                    },
                                }}
                            />
                        </Box>

                        {/* Контрастность */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ContrastIcon sx={{ fontSize: 14, color: '#0078d4' }} />
                                    <Typography variant="caption" sx={{ color: '#b0b0b0', fontSize: '0.7rem' }}>
                                        Контрастность
                                    </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: '#e0e0e0', fontSize: '0.7rem', fontWeight: 600, minWidth: '35px', textAlign: 'right' }}>
                                    {settings.contrast}%
                                </Typography>
                            </Box>
                            <Slider
                                value={settings.contrast}
                                onChange={(e, value) => handleSettingChange('contrast', value)}
                                min={0}
                                max={200}
                                step={1}
                                size="small"
                                sx={{
                                    color: '#0078d4',
                                    height: 4,
                                    '& .MuiSlider-thumb': {
                                        width: 14,
                                        height: 14,
                                        '&:hover': {
                                            boxShadow: '0 0 0 8px rgba(0, 120, 212, 0.16)',
                                        },
                                    },
                                    '& .MuiSlider-track': {
                                        height: 4,
                                    },
                                    '& .MuiSlider-rail': {
                                        height: 4,
                                        opacity: 0.3,
                                    },
                                }}
                            />
                        </Box>

                        {/* Насыщенность */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <PaletteIcon sx={{ fontSize: 14, color: '#0078d4' }} />
                                    <Typography variant="caption" sx={{ color: '#b0b0b0', fontSize: '0.7rem' }}>
                                        Насыщенность
                                    </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: '#e0e0e0', fontSize: '0.7rem', fontWeight: 600, minWidth: '35px', textAlign: 'right' }}>
                                    {settings.saturation}%
                                </Typography>
                            </Box>
                            <Slider
                                value={settings.saturation}
                                onChange={(e, value) => handleSettingChange('saturation', value)}
                                min={0}
                                max={200}
                                step={1}
                                size="small"
                                sx={{
                                    color: '#0078d4',
                                    height: 4,
                                    '& .MuiSlider-thumb': {
                                        width: 14,
                                        height: 14,
                                        '&:hover': {
                                            boxShadow: '0 0 0 8px rgba(0, 120, 212, 0.16)',
                                        },
                                    },
                                    '& .MuiSlider-track': {
                                        height: 4,
                                    },
                                    '& .MuiSlider-rail': {
                                        height: 4,
                                        opacity: 0.3,
                                    },
                                }}
                            />
                        </Box>

                        {/* Прозрачность */}
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <OpacityIcon sx={{ fontSize: 14, color: '#0078d4' }} />
                                    <Typography variant="caption" sx={{ color: '#b0b0b0', fontSize: '0.7rem' }}>
                                        Прозрачность
                                    </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: '#e0e0e0', fontSize: '0.7rem', fontWeight: 600, minWidth: '35px', textAlign: 'right' }}>
                                    {100 - settings.opacity}%
                                </Typography>
                            </Box>
                            <Slider
                                value={100 - settings.opacity}
                                onChange={(e, value) => handleSettingChange('opacity', 100 - value)}
                                min={0}
                                max={100}
                                step={1}
                                size="small"
                                sx={{
                                    color: '#0078d4',
                                    height: 4,
                                    '& .MuiSlider-thumb': {
                                        width: 14,
                                        height: 14,
                                        '&:hover': {
                                            boxShadow: '0 0 0 8px rgba(0, 120, 212, 0.16)',
                                        },
                                    },
                                    '& .MuiSlider-track': {
                                        height: 4,
                                    },
                                    '& .MuiSlider-rail': {
                                        height: 4,
                                        opacity: 0.3,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #333' }}>
                        <Button
                            size="small"
                            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                            onClick={() => {
                                const defaultSettings = { brightness: 100, contrast: 100, saturation: 100, opacity: 100 };
                                applyFilters(defaultSettings)
                                setSettings(defaultSettings);
                                dispatch(setMapSettings(defaultSettings));
                            }}
                            fullWidth
                            sx={{
                                color: '#b0b0b0',
                                fontSize: '0.7rem',
                                textTransform: 'none',
                                py: 0.5,
                                border: '1px solid #333',
                                '&:hover': {
                                    backgroundColor: '#2a2a2a',
                                    borderColor: '#0078d4',
                                    color: '#ffffff',
                                }
                            }}
                        >
                            Сбросить настройки
                        </Button>
                    </Box>
                </Menu>
                {/* Базовые слои */}
                <Box sx={{ mb: 0.5, display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 0.5 }}>

                    {baseLayers.map((layer) => (
                        <Button
                            key={layer.id}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleBaseLayerChange(layer.id);
                            }}
                            variant={baseLayer === layer.id ? 'contained' : 'outlined'}
                            size="small"
                            sx={{
                                mb: 0.2,
                                textTransform: 'none',
                                fontSize: '0.7rem',
                                width: 'auto',
                                minWidth: 'auto',
                                py: 0.1,
                                px: 0.8,
                                borderColor: '#444',
                                color: baseLayer === layer.id ? '#ffffff' : '#b0b0b0',
                                backgroundColor: baseLayer === layer.id ? '#0078d4' : 'transparent',
                                '&:hover': {
                                    backgroundColor: baseLayer === layer.id ? '#106ebe' : '#333',
                                    borderColor: '#555'
                                }
                            }}
                        >
                            {layer.name}
                        </Button>
                    ))}
                </Box>


                <Divider sx={{ p: 0, mt: 0.5, mb: 0.5, borderColor: '#333' }} />

                {/* Overlay слои */}
                <Typography variant="caption" sx={{
                    mb: 0.5,
                    fontWeight: 600,
                    display: 'flex',
                    justifyContent: 'start',
                    alignItems: 'center',
                    fontSize: '0.75rem',
                    color: '#b0b0b0'
                }}>
                    <LayersIcon sx={{ fontSize: 12, mr: 0.3 }} />
                    Слои данных
                </Typography>
                {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1 }}> */}
                {overlayLayersConfig.map((layer) => (
                    <Box key={layer.key} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0,
                        minHeight: '24px',
                        '&:hover': {
                            backgroundColor: '#2a2a2a',
                            borderRadius: 1
                        }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                            <Box sx={{ width: '24px', display: 'flex', justifyContent: 'center' }}>

                                {layer.icon}
                            </Box>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#e0e0e0' }}>
                                {layer.name}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {layer.count && <Chip
                                label={layer.count}
                                size="small"
                                variant="outlined"
                                sx={{
                                    height: '16px',
                                    fontSize: '0.75rem',
                                    minWidth: '20px',
                                    borderColor: '#555',
                                    color: '#b0b0b0',
                                    backgroundColor: '#333',
                                    '& .MuiChip-label': {
                                        px: 0.4
                                    }
                                }}
                            />}
                            <Switch
                                checked={Boolean(overlayLayers[layer.key])}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleOverlayLayerChange(layer.key);
                                }}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase': {
                                        padding: '3px',
                                    },
                                    '& .MuiSwitch-thumb': {
                                        width: 16,
                                        height: 16,
                                    },
                                    '& .MuiSwitch-track': {
                                        borderRadius: 8,
                                        backgroundColor: '#555',
                                    },
                                    '& .Mui-checked .MuiSwitch-thumb': {
                                        backgroundColor: '#0078d4',
                                    },
                                    '& .Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#0078d4',
                                        opacity: 0.5,
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                ))}

                <Divider sx={{ p: 0, mt: 0.5, mb: 0.5, borderColor: '#333' }} />

                {/* Data слои */}
                <Typography variant="caption" sx={{
                    // mb: 0.5,
                    fontWeight: 600,
                    display: 'flex',
                    justifyContent: 'start',
                    alignItems: 'center',
                    fontSize: '0.75rem',
                    color: '#b0b0b0'
                }}>
                    <LayersIcon sx={{ fontSize: 12, mr: 0.3 }} />
                    Слои статусов
                </Typography>
                {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1 }}> */}
                {dataLayersConfig.filter(dl => !dl.hide).map((layer) => (
                    <Box key={layer.key} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 0,
                        minHeight: '24px',
                        '&:hover': {
                            backgroundColor: '#2a2a2a',
                            borderRadius: 1
                        }
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
                            {layer.icon}
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#e0e0e0' }}>
                                {layer.name}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {layer.customSwitch && layer.customSwitch()}
                            {layer.count && <Chip
                                label={layer.count}
                                size="small"
                                variant="outlined"
                                sx={{
                                    height: '16px',
                                    fontSize: '0.75rem',
                                    minWidth: '20px',
                                    borderColor: '#555',
                                    color: '#b0b0b0',
                                    backgroundColor: '#333',
                                    '& .MuiChip-label': {
                                        px: 0.4
                                    }
                                }}
                            />}

                            <Switch
                                checked={Boolean(dataLayers[layer.key])}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleDataLayerChange(layer.key);
                                }}
                                disabled={layer.disable}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase': {
                                        padding: '3px',
                                    },
                                    '& .MuiSwitch-thumb': {
                                        width: 16,
                                        height: 16,
                                    },
                                    '& .MuiSwitch-track': {
                                        borderRadius: 8,
                                        backgroundColor: '#555',
                                    },
                                    '& .Mui-checked .MuiSwitch-thumb': {
                                        backgroundColor: '#0078d4',
                                    },
                                    '& .Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#0078d4',
                                        opacity: 0.5,
                                    }
                                }}
                            />
                        </Box>
                    </Box>
                ))}
                {/* </Box> */}
            </Box>
            ,
            document.body
        )
    );
};

export default ModalLayers; 